import { ayeApi } from '../../../core';
import { ICommonResponse } from '../../../core/api/http-methods/common-response.type';
import { PaymentConsents } from '../../../core/api/http-methods/get-payment-methods';

export interface DuitNowApprovalPollDeps {
  consentRef: string;
  /**
   * I1: 'fresh' — this consentRef came from a /pay call in THIS session, so it is known to
   * exist even if the list doesn't show it yet (until backend Part D ships, the list returns
   * ACTIVE rows only — a fresh ref's PENDING row simply isn't listed). 'resumed' — from a
   * `?duitnow_ref=` link, which could be stale, foreign, or already gone. A row that's never
   * been seen and isn't found only counts as "not found" (onNotFound) for 'resumed'; for
   * 'fresh' it's unresolved — keep polling and let the 5:00 "Still waiting" timeout handle it.
   */
  source: 'fresh' | 'resumed';
  /**
   * Returns false once the approval view has been closed — a poll already in flight (awaiting
   * the list) when that happens must not call any of the callbacks below — closing must never
   * produce a later popup (E1/E3/E13). The list re-fetch that already runs on close (E1) and on
   * `visibilitychange` (E3) covers "it got approved/declined after I left". This must reflect
   * the close the instant it happens, not just eventual unmount: Radix's dialog exit animation
   * can keep the caller's component mounted (and its unmount cleanup un-run) for ~200ms after
   * the user closes, so the caller should back this with a value updated during render (e.g. a
   * ref assigned in the component body), not only inside a `useEffect` cleanup.
   */
  isActive: () => boolean;
  /** Mutable across polls within one mount: true once a PENDING row has actually been observed
   * for this consentRef (see onNotFound vs onFailed below). */
  seenPendingRef: { current: boolean };
  onActive: () => void;
  onFailed: () => void;
  onNotFound: () => void;
  /** Called with every status the poll observes: the row's status, or 'NOT_LISTED'. */
  onStatus?: (status: string) => void;
  /** Injectable for testing; defaults to the real API. */
  fetchPaymentMethods?: () => Promise<unknown>;
}

/**
 * Builds the polling function passed to `usePolling` for the approval-pending screen. Pulled out
 * of the component so the close-race guard (`isActive`) and the seen-PENDING/declined-vs-
 * not-found logic (E8/E9) can be unit tested without mounting React.
 */
export function createDuitNowApprovalPoll({
  consentRef,
  source,
  isActive,
  seenPendingRef,
  onActive,
  onFailed,
  onNotFound,
  onStatus,
  fetchPaymentMethods = () => ayeApi.getPaymentMethods({})
}: DuitNowApprovalPollDeps): () => Promise<boolean> {
  return async () => {
    // Call the API directly: useApp().paymentMethods() returns [] on ANY failure
    // (usePortalHook.tsx:309-324), which would make a network blip look like "declined".
    // GetPaymentMethodsResponse is typed as the bare list (get-payment-methods.ts:7) but
    // the runtime body is { success, data } (usePortalHook.tsx:316-319 reads
    // response.data.payment_consents). Cast locally; other callers untouched.
    const res =
      (await fetchPaymentMethods()) as unknown as ICommonResponse<PaymentConsents>;

    // The view was closed while this request was in flight. `usePolling`'s own cleanup only
    // stops FUTURE ticks — it can't stop one already awaiting a response — so this check is the
    // only thing standing between "user closed the dialog" and a popup reopening it later.
    if (!isActive()) return true;

    if (!res?.success) return false; // E11: unknown → keep polling, never treat as declined
    const row = res.data.payment_consents.find(
      (m) => m.payment_consent_id === consentRef
    );
    onStatus?.(row?.status ?? 'NOT_LISTED');
    if (row?.status === 'ACTIVE') {
      onActive();
      return true;
    }
    if (row?.status === 'PENDING') {
      seenPendingRef.current = true;
      return false;
    }
    if (!row) {
      // E8/E9: only a row we've actually observed PENDING and then lost counts as "declined".
      if (seenPendingRef.current) {
        onFailed();
        return true;
      }
      // I1: a row we've never seen is unresolved, not declined. For a 'resumed' link that's
      // worth telling the user (it may be stale/foreign). For a 'fresh' consentRef — created by
      // /pay in this very session — it's known to exist; the list just doesn't carry PENDING
      // rows yet (pre backend Part D), so keep polling and let the 5:00 timeout handle it.
      if (source === 'resumed') {
        onNotFound();
        return true;
      }
      return false;
    }
    return false; // some other status (list = ACTIVE+PENDING only) — keep polling
  };
}
